import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, CheckCircle2Icon, HomeIcon, UserIcon, MapPinIcon, LoaderIcon, AlertCircleIcon, StarIcon, PhoneIcon } from 'lucide-react';
interface DaBellaQuoteFormProps {
  initialZip: string;
  serviceSlug: string;
  onClose: () => void;
}
export function DaBellaQuoteForm({
  initialZip,
  serviceSlug,
  onClose
}: DaBellaQuoteFormProps) {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [zipLookupLoading, setZipLookupLoading] = useState(false);
  const [zipLookupDone, setZipLookupDone] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [geoError, setGeoError] = useState('');
  const [editingZip, setEditingZip] = useState(false);
  const getProductId = (slug: string): string => {
    switch (slug) {
      case 'roofing':
        return 'Roof';
      case 'windows':
        return 'Windows';
      case 'siding':
        return 'Siding';
      case 'baths':
        return 'Bath';
      case 'walk-in-tubs':
        return 'Walk in tubs';
      default:
        return 'Windows';
    }
  };
  const getServiceLabel = (slug: string): string => {
    switch (slug) {
      case 'roofing':
        return 'Roofing';
      case 'windows':
        return 'Windows';
      case 'siding':
        return 'Siding';
      case 'baths':
        return 'Bathroom';
      case 'walk-in-tubs':
        return 'Walk-in Tubs';
      default:
        return 'Home Improvement';
    }
  };
  const [formData, setFormData] = useState({
    BDMLeadType: 'test',
    alid: '2512',
    oid: '10245',
    product_id_dabel: getProductId(serviceSlug),
    log_number_dabel: '2',
    price: '75',
    trustedform_cert_url: '',
    formName: 'DaBella_form_v1',
    first_name: '',
    last_name: '',
    phone_1: '',
    email: '',
    address_1: '',
    city: '',
    state: '',
    postal_code: initialZip || '',
    project_details: '',
    homeOwnership: ''
  });
  // Zip code lookup
  useEffect(() => {
    const zip = formData.postal_code;
    if (zip && zip.length === 5) {
      setZipLookupLoading(true);
      setZipLookupDone(false);
      setGeoError('');
      fetch(`https://api.zippopotam.us/us/${zip}`).then(res => {
        if (!res.ok) throw new Error('Invalid zip');
        return res.json();
      }).then(data => {
        if (data.places && data.places.length > 0) {
          const place = data.places[0];
          const stateAbbr = place['state abbreviation'];
          setFormData(prev => ({
            ...prev,
            city: place['place name'] || '',
            state: stateAbbr || ''
          }));
          if (stateAbbr !== 'ID' && stateAbbr !== 'UT') {
            setGeoError('DaBella services are currently only available in Idaho and Utah.');
          }
          setZipLookupDone(true);
        }
      }).catch(() => {
        setZipLookupDone(false);
      }).finally(() => {
        setZipLookupLoading(false);
      });
    }
  }, [formData.postal_code]);
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData(prev => ({
      ...prev,
      phone_1: digits
    }));
  };
  // TrustedForm
  useEffect(() => {
    try {
      const tf = document.createElement('script');
      tf.type = 'text/javascript';
      tf.async = true;
      tf.src = 'https://api.trustedform.com/trustedform.js?provide_referrer=false&field=xxTrustedFormCertUrl&l=' + new Date().getTime() + Math.random();
      document.head.appendChild(tf);
    } catch (e) {
      console.warn('TrustedForm script could not be loaded:', e);
    }
    const interval = setInterval(() => {
      try {
        const certInput = document.querySelector('input[name="xxTrustedFormCertUrl"]') as HTMLInputElement;
        if (certInput && certInput.value) {
          setFormData(prev => ({
            ...prev,
            trustedform_cert_url: certInput.value
          }));
          clearInterval(interval);
        }
      } catch (e) {
        // continue polling
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const totalSteps = 4;
  const progress = step / totalSteps * 100;
  const handleNext = () => {
    setDirection(1);
    setStep(prev => Math.min(prev + 1, totalSteps));
  };
  const handleBack = () => {
    setDirection(-1);
    setStep(prev => Math.max(prev - 1, 1));
  };
  const handleOptionSelect = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setTimeout(() => handleNext(), 300);
  };
  const validateAddress = (address: string): boolean => {
    const addressPattern = /^\d+\s+\S{2,}/;
    return addressPattern.test(address.trim());
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === 'address_1') {
      setAddressError('');
    }
  };
  const handleAddressBlur = () => {
    if (formData.address_1 && !validateAddress(formData.address_1)) {
      setAddressError('Please enter a valid street address (e.g. 123 Main St)');
    } else {
      setAddressError('');
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const certInput = document.querySelector('input[name="xxTrustedFormCertUrl"]') as HTMLInputElement;
    const finalCert = certInput?.value || formData.trustedform_cert_url;
    const payload = {
      ...formData,
      trustedform_cert_url: finalCert
    };
    // Submit to Zapier webhook
    const params = new URLSearchParams();
    Object.entries(payload).forEach(([key, value]) => {
      if (value) params.append(key, String(value));
    });
    const webhookUrl = `https://hooks.zapier.com/hooks/catch/6973915/uxzbmya/?${params.toString()}`;
    console.log('Submitting DaBella lead:', webhookUrl);
    try {
      await fetch(webhookUrl, {
        method: 'GET',
        mode: 'no-cors'
      });
    } catch (err) {
      console.warn('Webhook call failed, continuing:', err);
    }
    // Also submit to BDM lead management
    const bdmParams = new URLSearchParams();
    bdmParams.append('BDMLeadType', formData.BDMLeadType);
    bdmParams.append('alid', formData.alid);
    bdmParams.append('oid', formData.oid);
    bdmParams.append('first_name', formData.first_name);
    bdmParams.append('last_name', formData.last_name);
    bdmParams.append('phone_1', formData.phone_1);
    bdmParams.append('email', formData.email);
    bdmParams.append('address_1', formData.address_1);
    bdmParams.append('city', formData.city);
    bdmParams.append('state', formData.state);
    bdmParams.append('postal_code', formData.postal_code);
    bdmParams.append('product_id_dabel', formData.product_id_dabel);
    bdmParams.append('log_number_dabel', formData.log_number_dabel);
    bdmParams.append('price', formData.price);
    bdmParams.append('trustedform_cert_url', finalCert);
    const bdmUrl = `http://bdmleadmanagement.valid1.net/Leads.aspx?${bdmParams.toString()}`;
    console.log('Submitting to BDM:', bdmUrl);
    try {
      await fetch(bdmUrl, {
        method: 'POST',
        mode: 'no-cors'
      });
    } catch (err) {
      console.warn('BDM post failed, continuing:', err);
    }
    setIsSubmitting(false);
    setIsSuccess(true);
  };
  const getNextBusinessDay = () => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    if (date.getDay() === 6) date.setDate(date.getDate() + 2);
    if (date.getDay() === 0) date.setDate(date.getDate() + 1);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir < 0 ? 50 : -50,
      opacity: 0
    })
  };
  // Service-specific question data
  const getServiceQuestion = () => {
    switch (serviceSlug) {
      case 'roofing':
        return {
          question: 'What type of roof do you currently have?',
          options: [{
            value: 'Asphalt Shingle',
            label: 'Asphalt Shingle'
          }, {
            value: 'Metal',
            label: 'Metal'
          }, {
            value: 'Tile',
            label: 'Tile'
          }, {
            value: 'Other',
            label: 'Other / Not Sure'
          }]
        };
      case 'windows':
        return {
          question: 'How many windows do you need to replace?',
          options: [{
            value: '1-2',
            label: '1–2 Windows'
          }, {
            value: '3-5',
            label: '3–5 Windows'
          }, {
            value: '6-9',
            label: '6–9 Windows'
          }, {
            value: '10+',
            label: '10+ Windows'
          }]
        };
      case 'siding':
        return {
          question: 'What type of siding do you currently have?',
          options: [{
            value: 'Vinyl',
            label: 'Vinyl'
          }, {
            value: 'Wood',
            label: 'Wood'
          }, {
            value: 'Aluminum',
            label: 'Aluminum'
          }, {
            value: 'Other',
            label: 'Other / Not Sure'
          }]
        };
      case 'baths':
        return {
          question: 'What type of bathroom project are you looking for?',
          options: [{
            value: 'Tub to Shower',
            label: 'Tub to Shower Conversion'
          }, {
            value: 'Shower Replacement',
            label: 'Shower Replacement'
          }, {
            value: 'Tub Replacement',
            label: 'Bathtub Replacement'
          }, {
            value: 'Full Remodel',
            label: 'Full Bathroom Remodel'
          }]
        };
      case 'walk-in-tubs':
        return {
          question: 'Who is the walk-in tub for?',
          options: [{
            value: 'Myself',
            label: 'Myself'
          }, {
            value: 'Family Member',
            label: 'A Family Member'
          }, {
            value: 'Planning Ahead',
            label: 'Planning for the Future'
          }]
        };
      default:
        return {
          question: 'Tell us about your project',
          options: [{
            value: 'Replace',
            label: 'Replacement'
          }, {
            value: 'Repair',
            label: 'Repair'
          }, {
            value: 'Not Sure',
            label: 'Not Sure'
          }]
        };
    }
  };
  const DaBellaHeader = ({
    hideExit = false
  }: {
    hideExit?: boolean;
  }) => <header className="bg-white border-b border-gray-200 py-4 px-6 flex justify-between items-center sticky top-0 z-10" data-id="element-1127">
      <div className="flex flex-col" data-id="element-1128">
        <div className="flex items-baseline gap-2 mb-1" data-id="element-1129">
          <img src="https://cdn.magicpatterns.com/uploads/qpzVzhypzNbXmPMfecWDrq/debella_logo.svg" alt="DaBella" className="h-8 md:h-10" data-id="element-1130" />
        </div>
        <span className="text-[10px] md:text-xs font-semibold text-gray-600" data-id="element-1131">
          {getServiceLabel(serviceSlug)} Estimate
        </span>
      </div>
      <div className="flex items-center gap-6" data-id="element-1132">
        {!hideExit && <button onClick={onClose} className="text-sm font-medium text-gray-500 hover:text-[#1B5E20] transition-colors flex items-center gap-1" data-id="element-1133">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-1134" />
            <span className="hidden sm:inline" data-id="element-1135">Exit</span>
          </button>}
      </div>
    </header>;
  if (isSuccess) {
    return <div className="min-h-screen bg-white flex flex-col font-sans" data-id="element-1136">
        <DaBellaHeader hideExit data-id="element-1137" />

        {/* Hero Banner — Phone Call Focused */}
        <div className="relative w-full h-64 md:h-80 flex items-center" data-id="element-1138">
          <div className="absolute inset-0 z-0" data-id="element-1139">
            <img src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" alt="Home improvement professional" className="w-full h-full object-cover" data-id="element-1140" />
          </div>
          <div className="absolute inset-0 z-10" data-id="element-1141">
            <div className="absolute left-0 top-0 bottom-0 w-full md:w-2/3 bg-[#1B5E20]/90 flex flex-col justify-center px-8 md:px-16" data-id="element-1142">
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight" data-id="element-1143">
                Your Quote Request Has
                <br data-id="element-1144" />
                Been Submitted!
              </h1>
              <p className="text-lg md:text-xl text-white/90" data-id="element-1145">
                A DaBella representative will be calling you shortly.
              </p>
            </div>
          </div>
        </div>

        {/* Keep Your Phone Nearby Section */}
        <div className="max-w-3xl mx-auto w-full px-6 py-16 text-center" data-id="element-1146">
          <div className="flex justify-center mb-6" data-id="element-1147">
            <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center" data-id="element-1148">
              <PhoneIcon className="w-10 h-10 text-[#1B5E20]" data-id="element-1149" />
            </div>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6" data-id="element-1150">
            Keep your phone nearby!
          </h2>
          <p className="text-gray-600 leading-relaxed max-w-xl mx-auto" data-id="element-1151">
            A DaBella representative will be reaching out to you at{' '}
            <span className="font-semibold text-gray-900" data-id="element-1152">
              {formData.phone_1}
            </span>{' '}
            to discuss your {getServiceLabel(serviceSlug).toLowerCase()} project
            and schedule your free in-home estimate. The call typically comes
            within one business day.
          </p>
        </div>

        {/* Homeowner Info — Expanded with form answers */}
        <div className="max-w-4xl mx-auto w-full px-6 py-12 text-center border-b border-gray-200" data-id="element-1153">
          <h2 className="text-[#1B5E20] font-bold text-sm uppercase tracking-wider mb-6" data-id="element-1154">
            Your Submission Details
          </h2>
          <div className="text-gray-900 text-lg mb-2" data-id="element-1155">
            <p className="font-semibold text-xl" data-id="element-1156">
              {formData.first_name} {formData.last_name}
            </p>
            <p data-id="element-1157">{formData.address_1}</p>
            <p data-id="element-1158">
              {formData.city}, {formData.state} {formData.postal_code}
            </p>
            <p data-id="element-1159">{formData.phone_1}</p>
            <p className="text-gray-500" data-id="element-1160">{formData.email}</p>
          </div>

          <div className="mt-6 inline-flex flex-col gap-2 text-sm text-gray-600" data-id="element-1161">
            <div className="flex items-center justify-center gap-2" data-id="element-1162">
              <span className="font-semibold text-gray-800" data-id="element-1163">Service:</span>
              <span data-id="element-1164">{getServiceLabel(serviceSlug)}</span>
            </div>
            <div className="flex items-center justify-center gap-2" data-id="element-1165">
              <span className="font-semibold text-gray-800" data-id="element-1166">
                Project Details:
              </span>
              <span data-id="element-1167">{formData.project_details || '—'}</span>
            </div>
            <div className="flex items-center justify-center gap-2" data-id="element-1168">
              <span className="font-semibold text-gray-800" data-id="element-1169">Homeowner:</span>
              <span data-id="element-1170">{formData.homeOwnership || '—'}</span>
            </div>
          </div>
        </div>

        {/* What to Expect Section */}
        <div className="max-w-6xl mx-auto w-full px-6 py-12" data-id="element-1171">
          <div className="flex flex-col md:flex-row gap-8" data-id="element-1172">
            <div className="w-full md:w-1/2 relative min-h-[300px] md:min-h-[400px] rounded-lg overflow-hidden" data-id="element-1173">
              <img src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Couple reviewing documents" className="absolute inset-0 w-full h-full object-cover" data-id="element-1174" />
              <div className="absolute inset-0 bg-black/40 flex items-center p-10" data-id="element-1175">
                <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight" data-id="element-1176">
                  What to Expect
                  <br data-id="element-1177" />
                  During Your Free In-
                  <br data-id="element-1178" />
                  Home Estimate
                </h2>
              </div>
            </div>
            <div className="w-full md:w-1/2 flex flex-col gap-4" data-id="element-1179">
              <div className="bg-gray-50 p-8 rounded-lg" data-id="element-1180">
                <h3 className="text-[#1B5E20] font-bold text-lg mb-3" data-id="element-1181">
                  Explore Products
                </h3>
                <p className="text-gray-700" data-id="element-1182">
                  Your DaBella representative will walk you through our premium
                  product options, custom styles and colors, and exclusive
                  warranties. They'll also help you compare the value of our
                  products to that of other providers.
                </p>
              </div>
              <div className="bg-white border border-gray-100 shadow-lg p-8 rounded-lg -ml-0 md:-ml-12 relative z-10" data-id="element-1183">
                <h3 className="text-[#1B5E20] font-bold text-lg mb-3" data-id="element-1184">
                  Review Price & Financing
                </h3>
                <p className="text-gray-700" data-id="element-1185">
                  Together, we'll review current promotions, calculate your
                  estimated monthly payment and energy bill savings, and
                  determine the best financing options for your project.
                </p>
              </div>
              <div className="bg-gray-50 p-8 rounded-lg" data-id="element-1186">
                <h3 className="text-[#1B5E20] font-bold text-lg mb-3" data-id="element-1187">
                  Get a Same Day Quote
                </h3>
                <p className="text-gray-700" data-id="element-1188">
                  You'll get an exact, no-obligation quote for both your new
                  products and installation, and we'll discuss next steps in the
                  home transformation process.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <section className="py-20 bg-[#FAFAFA]" data-id="element-1189">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1190">
            <div className="text-center mb-12" data-id="element-1191">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900" data-id="element-1192">
                What Our Customers Say
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1193">
              {[{
              name: 'Mike R.',
              location: 'Boise, ID',
              text: 'DaBella replaced our entire roof in just two days. The crew was professional, clean, and the new roof looks incredible. Highly recommend!'
            }, {
              name: 'Jennifer & Tom S.',
              location: 'Salt Lake City, UT',
              text: 'We had 14 windows replaced and the difference is night and day. Our energy bills dropped significantly and the house feels so much quieter.'
            }, {
              name: 'Carol W.',
              location: 'Meridian, ID',
              text: 'The walk-in tub installation was seamless. The team was respectful of our home and the tub has been life-changing for my mobility.'
            }].map((review, idx) => <div key={idx} className="bg-white p-8 rounded-xl shadow-sm border border-gray-200" data-id="element-1194">
                  <div className="flex text-yellow-400 mb-4" data-id="element-1195">
                    {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-5 h-5 fill-current" data-id="element-1196" />)}
                  </div>
                  <p className="text-gray-600 mb-6 italic" data-id="element-1197">
                    &ldquo;{review.text}&rdquo;
                  </p>
                  <div data-id="element-1198">
                    <p className="font-bold text-gray-900" data-id="element-1199">{review.name}</p>
                    <p className="text-sm text-gray-500" data-id="element-1200">{review.location}</p>
                  </div>
                </div>)}
            </div>
          </div>
        </section>

        {/* DaBella Footer */}
        <footer className="bg-[#1B5E20] py-16" data-id="element-1201">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1202">
            <div className="flex flex-col md:flex-row justify-between items-start gap-12 mb-16" data-id="element-1203">
              <div className="flex flex-col gap-8" data-id="element-1204">
                <div className="flex flex-col" data-id="element-1205">
                  <img src="https://cdn.magicpatterns.com/uploads/qpzVzhypzNbXmPMfecWDrq/debella_logo.svg" alt="DaBella" className="h-8 brightness-0 invert mb-2 object-contain object-left" data-id="element-1206" />
                  <span className="text-[10px] font-semibold text-white/90" data-id="element-1207">
                    Roofing &bull; Windows &bull; Siding &bull; Baths
                  </span>
                </div>
                <p className="text-xs text-white/80 font-medium" data-id="element-1208">
                  &copy; {new Date().getFullYear()} findservicequotes.io. All
                  Rights Reserved.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-16" data-id="element-1209">
                <div className="flex flex-col gap-4" data-id="element-1210">
                  <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-1211">
                    Home
                  </a>
                  <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-1212">
                    Privacy Policy
                  </a>
                  <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-1213">
                    Terms Of Use
                  </a>
                </div>
                <div className="flex flex-col gap-4" data-id="element-1214">
                  <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-1215">
                    Accessibility Policy
                  </a>
                  <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-1216">
                    Do Not Sell My Personal Information
                  </a>
                </div>
              </div>
            </div>
            <div className="border-t border-white/20 pt-8" data-id="element-1217">
              <p className="text-[10px] text-white/60 leading-relaxed text-justify" data-id="element-1218">
                DaBella services are currently available in Idaho and Utah. All
                estimates are free and no-obligation. Actual project costs may
                vary based on scope, materials, and local conditions. Licensed
                and insured. Contact us for full warranty details.
              </p>
            </div>
          </div>
        </footer>
      </div>;
  }
  const serviceQ = getServiceQuestion();
  return <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-id="element-1219">
      <DaBellaHeader data-id="element-1220" />
      <input type="hidden" name="xxTrustedFormCertUrl" data-id="element-1221" />

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 h-2" data-id="element-1222">
        <motion.div className="bg-[#1B5E20] h-full" initial={{
        width: 0
      }} animate={{
        width: `${progress}%`
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} data-id="element-1223" />
      </div>

      <main className="flex-1 flex flex-col items-center pt-8 pb-20 px-4" data-id="element-1224">
        <div className="w-full max-w-2xl" data-id="element-1225">
          <div className="mb-6 h-8" data-id="element-1226">
            {step > 1 && <button onClick={handleBack} className="text-gray-500 hover:text-[#1B5E20] font-medium flex items-center gap-2 transition-colors" data-id="element-1227">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-1228" /> Back
              </button>}
          </div>

          <div className="relative bg-white rounded-2xl shadow-lg p-6 md:p-10 border border-gray-100" data-id="element-1229">
            <AnimatePresence mode="wait" custom={direction} data-id="element-1230">
              {/* STEP 1: Service-Specific Question */}
              {step === 1 && <motion.div key="step1" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-1231">
                  <h1 className="text-2xl md:text-3xl font-bold text-[#1B5E20] mb-8 text-center" data-id="element-1232">
                    {serviceQ.question}
                  </h1>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1233">
                    {serviceQ.options.map((opt, idx) => <button key={idx} onClick={() => handleOptionSelect('project_details', opt.value)} className={`p-6 rounded-xl border-2 text-center font-bold text-lg transition-all duration-200 ${formData.project_details === opt.value ? 'border-[#1B5E20] bg-green-50 text-[#1B5E20] shadow-md' : 'border-gray-200 bg-white text-gray-700 hover:border-green-300 hover:shadow-sm'}`} data-id="element-1234">
                        {opt.label}
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 2: Homeownership */}
              {step === 2 && <motion.div key="step2" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-1235">
                  <h1 className="text-2xl md:text-3xl font-bold text-[#1B5E20] mb-8 text-center" data-id="element-1236">
                    Do you own your home?
                  </h1>
                  <div className="flex flex-col gap-4" data-id="element-1237">
                    {[{
                  value: 'Yes',
                  desc: 'I own this property'
                }, {
                  value: 'No',
                  desc: 'I rent this property'
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('homeOwnership', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.homeOwnership === option.value ? 'border-[#1B5E20] bg-green-50 shadow-md' : 'border-gray-200 bg-white hover:border-green-300 hover:shadow-sm'}`} data-id="element-1238">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.homeOwnership === option.value ? 'bg-green-100 text-[#1B5E20]' : 'bg-gray-50 text-gray-500'}`} data-id="element-1239">
                          <HomeIcon className="w-6 h-6" data-id="element-1240" />
                        </div>
                        <div data-id="element-1241">
                          <div className="font-bold text-gray-900 text-lg" data-id="element-1242">
                            {option.value}
                          </div>
                          <div className="text-gray-500 text-sm" data-id="element-1243">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 3: Address */}
              {step === 3 && <motion.div key="step3" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-1244">
                  <h1 className="text-2xl md:text-3xl font-bold text-[#1B5E20] mb-8 text-center" data-id="element-1245">
                    Where is your project located?
                  </h1>

                  <div className="space-y-5" data-id="element-1246">
                    <div data-id="element-1247">
                      <label htmlFor="address_1" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-1248">
                        Street Address
                      </label>
                      <input type="text" id="address_1" name="address_1" value={formData.address_1} onChange={handleInputChange} onBlur={handleAddressBlur} className={`w-full h-14 px-4 bg-gray-50 border rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1B5E20] focus:border-transparent ${addressError ? 'border-red-400 bg-red-50' : 'border-gray-300'}`} placeholder="123 Main St" data-id="element-1249" />
                      {addressError && <p className="text-red-500 text-xs mt-1.5 font-medium" data-id="element-1250">
                          {addressError}
                        </p>}

                      <div className="mt-3 flex flex-col gap-2" data-id="element-1251">
                        {zipLookupLoading ? <div className="flex items-center gap-2 text-gray-400 text-sm" data-id="element-1252">
                            <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-1253" />
                            <span data-id="element-1254">Looking up location...</span>
                          </div> : editingZip ? <div className="flex items-center gap-2 w-full" data-id="element-1255">
                            <MapPinIcon className="w-4 h-4 text-[#1B5E20] flex-shrink-0" data-id="element-1256" />
                            <input type="text" value={formData.postal_code} onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                        setFormData(prev => ({
                          ...prev,
                          postal_code: val,
                          city: '',
                          state: ''
                        }));
                        setZipLookupDone(false);
                        setGeoError('');
                      }} onBlur={() => {
                        if (formData.postal_code.length === 5) setEditingZip(false);
                      }} onKeyDown={e => {
                        if (e.key === 'Enter' && formData.postal_code.length === 5) setEditingZip(false);
                      }} placeholder="Enter ZIP" maxLength={5} autoFocus className="flex-1 h-10 px-3 bg-white border border-green-300 rounded-lg text-sm font-semibold text-[#1B5E20] focus:outline-none focus:ring-2 focus:ring-[#1B5E20] focus:border-transparent" data-id="element-1257" />
                          </div> : zipLookupDone && formData.city && formData.state ? <button type="button" onClick={() => setEditingZip(true)} className={`flex items-center gap-2 border rounded-lg px-3 py-2 w-full transition-colors cursor-pointer text-left ${geoError ? 'bg-red-50 border-red-200 hover:bg-red-100' : 'bg-green-50 border-green-200 hover:bg-green-100'}`} title="Click to change ZIP code" data-id="element-1258">
                            <MapPinIcon className={`w-4 h-4 flex-shrink-0 ${geoError ? 'text-red-500' : 'text-[#1B5E20]'}`} data-id="element-1259" />
                            <span className={`text-sm font-semibold ${geoError ? 'text-red-700' : 'text-[#1B5E20]'}`} data-id="element-1260">
                              {formData.city}, {formData.state}{' '}
                              {formData.postal_code}
                            </span>
                            <span className="text-[10px] text-gray-400 ml-auto" data-id="element-1261">
                              edit
                            </span>
                          </button> : <button type="button" onClick={() => setEditingZip(true)} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 w-full hover:bg-gray-100 transition-colors cursor-pointer text-left" title="Click to enter ZIP code" data-id="element-1262">
                            <MapPinIcon className="w-4 h-4 text-gray-400" data-id="element-1263" />
                            <span className="text-sm text-gray-500" data-id="element-1264">
                              {formData.postal_code ? `Zip: ${formData.postal_code}` : 'Enter your ZIP code'}
                            </span>
                          </button>}

                        {geoError && <div className="flex items-start gap-2 text-red-600 bg-red-50 p-3 rounded-lg border border-red-100 mt-2" data-id="element-1265">
                            <AlertCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-1266" />
                            <p className="text-sm font-medium" data-id="element-1267">{geoError}</p>
                          </div>}
                      </div>
                    </div>

                    <button onClick={() => {
                  if (!validateAddress(formData.address_1)) {
                    setAddressError('Please enter a valid street address (e.g. 123 Main St)');
                    return;
                  }
                  handleNext();
                }} disabled={!formData.address_1 || !formData.city || !formData.state || !formData.postal_code || !!addressError || !!geoError} className="w-full mt-6 h-14 bg-[#1B5E20] hover:bg-green-800 text-white font-bold text-lg rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1268">
                      Continue
                    </button>
                  </div>
                </motion.div>}

              {/* STEP 4: Contact Information */}
              {step === 4 && <motion.div key="step4" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-1269">
                  <h1 className="text-2xl md:text-3xl font-bold text-[#1B5E20] mb-2 text-center" data-id="element-1270">
                    Who should we send the quote to?
                  </h1>
                  <p className="text-gray-500 text-center mb-8" data-id="element-1271">
                    Last step! Your information is secure.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-5" data-id="element-1272">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-1273">
                      <div data-id="element-1274">
                        <label htmlFor="first_name" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-1275">
                          First Name
                        </label>
                        <input type="text" id="first_name" name="first_name" required value={formData.first_name} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1B5E20] focus:border-transparent" placeholder="First Name" data-id="element-1276" />
                      </div>
                      <div data-id="element-1277">
                        <label htmlFor="last_name" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-1278">
                          Last Name
                        </label>
                        <input type="text" id="last_name" name="last_name" required value={formData.last_name} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1B5E20] focus:border-transparent" placeholder="Last Name" data-id="element-1279" />
                      </div>
                    </div>

                    <div data-id="element-1280">
                      <label htmlFor="email" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-1281">
                        Email Address
                      </label>
                      <input type="email" id="email" name="email" required value={formData.email} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1B5E20] focus:border-transparent" placeholder="email@example.com" data-id="element-1282" />
                    </div>

                    <div data-id="element-1283">
                      <label htmlFor="phone_1" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-1284">
                        Phone Number
                      </label>
                      <input type="tel" id="phone_1" name="phone_1" required value={formData.phone_1} onChange={handlePhoneChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1B5E20] focus:border-transparent" placeholder="0000000000" maxLength={10} data-id="element-1285" />
                      <p className="text-xs text-gray-500 mt-1" data-id="element-1286">
                        Format: 10 digits only
                      </p>
                    </div>

                    <div className="pt-4" data-id="element-1287">
                      <label className="flex items-start gap-3 mb-4 cursor-pointer" data-id="element-1288">
                        <input type="checkbox" checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)} className="mt-0.5 w-5 h-5 rounded border-gray-300 text-[#1B5E20] focus:ring-[#1B5E20] cursor-pointer flex-shrink-0" required data-id="element-1289" />
                        <span className="text-[11px] text-gray-500 leading-tight" data-id="element-1290">
                          By clicking the button, I provide my electronic
                          signature and express written consent to share my
                          contact information with DaBella, including their
                          contractors and affiliates, and up to four home
                          service companies. I authorize them to email, call, or
                          text me for marketing purposes using autodialer
                          technology, AI generated or AI assisted voice, or
                          prerecorded voice messages on the phone number I
                          provided, even if listed on a Do Not Call list.
                          Consent is not a condition to purchase.
                        </span>
                      </label>
                      <button type="submit" disabled={isSubmitting || formData.phone_1.length < 10 || !consentChecked} className="w-full h-16 bg-[#1B5E20] hover:bg-green-800 text-white font-bold text-xl rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed shadow-lg shadow-green-900/20" data-id="element-1291">
                        {isSubmitting ? 'Processing...' : 'Get Your Free Quote'}
                      </button>
                    </div>
                  </form>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>;
}